import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/compras/notas-compra-page.tsx — Fase 13, Plano 22 (COMP-03, D-15, D-17).
// Lista "Notas de Compra" com 2 abas: "Notas" e "Compras sem pedido ({N})". Situação, pedidos atendidos e quem recebeu vêm do
// servidor (GET /api/v1/compras/notas); filtros de Fornecedor, Situação e Período (De/Até) são resolvidos no servidor e
// espelhados na URL junto com a aba. "Importar XML" (só compras:gerenciar) abre o diálogo de importação de 1..N arquivos.
import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, FileClock, RefreshCw, Undo2, Upload, X } from 'lucide-react';
import { SITUACOES_NOTA_COMPRA, type AbaNotasCompra, type ListaNotasCompra, type NotaCompraResumo, type SituacaoNotaCompra } from '@pieltec/contracts';
import { FornecedorCombobox } from '../../components/compras/fornecedor-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { urlConferencia } from '../../lib/compras/importacao-xml';
import {
  apresentacaoSituacaoNota,
  contagemNotas,
  escreverFiltrosNotas,
  estadoDaListaNotas,
  filtrosNotasAtivos,
  filtrosNotasVazios,
  lerFiltrosNotas,
  montarQueryNotasCompra,
  rotuloAbaSemPedido,
  rotuloSituacaoNota,
  type FiltrosNotasCompra,
  type IconeSituacaoNota,
} from '../../lib/compras/notas';
import { obterSessaoAtual } from '../../lib/session';
import { formatarData, formatarMoeda } from '../../lib/vendas/formatacao';
import { ImportarNotaDialog } from './importar-nota-dialog';
import { CampoSelect } from '../../components/ui/campo-select';

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ICONES_SITUACAO: Record<IconeSituacaoNota, typeof FileClock> = { FileClock, CheckCircle2, Undo2 };

export function NotasCompraPage() {
  const [filtros, setFiltros] = useState<FiltrosNotasCompra>(() => lerFiltrosNotas(window.location.search));
  const [dados, setDados] = useState<ListaNotasCompra | null>(null);
  const [totalSemPedido, setTotalSemPedido] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [importando, setImportando] = useState(false);

  // A autorização real é do servidor (compras:gerenciar); aqui o botão só aparece para quem pode.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPodeGerenciar(sessao.autenticado && sessao.usuario.permissions.includes('compras:gerenciar'));
    });
    return () => {
      vivo = false;
    };
  }, []);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/compras/notas?${montarQueryNotasCompra(filtros, pagina)}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaNotasCompra;
        if (!sinal || sinal.vivo) {
          setDados(json);
          if (filtros.aba === 'SEM_PEDIDO') setTotalSemPedido(json.total);
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
      // O N da aba "Compras sem pedido" vem de uma consulta própria (melhor esforço: se falhar, a aba fica sem número).
      if (filtros.aba === 'TODAS') {
        try {
          const res = await fetch(`/api/v1/compras/notas?${montarQueryNotasCompra(filtros, 1, 'SEM_PEDIDO')}`, { credentials: 'include' });
          if (res.ok) {
            const json = (await res.json()) as ListaNotasCompra;
            if (!sinal || sinal.vivo) setTotalSemPedido(json.total);
          }
        } catch {
          if (!sinal || sinal.vivo) setTotalSemPedido(null);
        }
      }
    },
    [filtros, pagina],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  // Aba e filtros na URL: recarregar ou compartilhar o link preserva o recorte (replaceState não empilha histórico).
  useEffect(() => {
    const query = escreverFiltrosNotas(filtros);
    const destino = `${window.location.pathname}${query ? `?${query}` : ''}`;
    if (destino !== `${window.location.pathname}${window.location.search}`) window.history.replaceState(null, '', destino);
  }, [filtros]);

  function alterarFiltros(parcial: Partial<FiltrosNotasCompra>): void {
    setFiltros((atual) => ({ ...atual, ...parcial }));
    setPagina(1);
  }

  function limparFiltros(): void {
    setFiltros((atual) => filtrosNotasVazios(atual.aba));
    setPagina(1);
  }

  const total = dados?.total ?? 0;
  const tamanhoPagina = dados?.tamanhoPagina ?? 50;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));
  const comFiltro = filtrosNotasAtivos(filtros);
  const estado = estadoDaListaNotas({ erro, carregando, temDados: dados !== null, total, comFiltro });
  const semPedido = filtros.aba === 'SEM_PEDIDO';
  const botaoImportar = podeGerenciar ? (
    <Button type="button" onClick={() => setImportando(true)}>
      <Upload aria-hidden="true" />
      Importar XML
    </Button>
  ) : null;

  const corpo = (
    <div className="flex flex-col gap-4">
      {semPedido && <p className="text-sm text-muted-foreground">Notas recebidas sem pedido de compra, com a justificativa informada no recebimento.</p>}
      {estado === 'erro' ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar as notas de compra. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : estado === 'carregando' ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando notas de compra">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : estado === 'vazio' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            {semPedido ? (
              <h2 className="text-xl font-semibold leading-tight">Nenhuma compra sem pedido.</h2>
            ) : (
              <>
                <h2 className="text-xl font-semibold leading-tight">Nenhuma NF de compra</h2>
                <p className="max-w-xl text-sm text-muted-foreground">Importe o XML da nota do fornecedor para conferir e dar entrada no estoque.</p>
                {botaoImportar}
              </>
            )}
          </CardContent>
        </Card>
      ) : estado === 'sem-resultado' ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhuma nota encontrada</h2>
            <p className="text-sm text-muted-foreground">Ajuste ou limpe os filtros.</p>
            <Button type="button" variant="outline" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">{semPedido ? 'Compras sem pedido' : 'Notas de compra'}</caption>
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th scope="col" className={CABECALHO}>NF</th>
                  <th scope="col" className={CABECALHO}>Fornecedor</th>
                  <th scope="col" className={CABECALHO}>Emissão</th>
                  <th scope="col" className={`${CABECALHO} text-right`}>Valor</th>
                  {semPedido ? (
                    <>
                      <th scope="col" className={CABECALHO}>Justificativa</th>
                      <th scope="col" className={CABECALHO}>Recebida por</th>
                    </>
                  ) : (
                    <>
                      <th scope="col" className={CABECALHO}>Pedido(s)</th>
                      <th scope="col" className={CABECALHO}>Situação</th>
                    </>
                  )}
                  <th scope="col" className="px-3 py-2">
                    <span className="sr-only">Abrir</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {dados?.itens.map((nota) => (
                  <LinhaNota key={nota.id} nota={nota} semPedido={semPedido} />
                ))}
              </tbody>
            </table>
          </div>
          <nav aria-label="Paginação da tabela" className="flex items-center justify-between gap-3 border-t bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
            <span className="tabular-nums" aria-live="polite">
              Página {pagina} de {totalPaginas} · {contagemNotas(total)}
            </span>
            <div className="flex items-center gap-1">
              <Button type="button" variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} aria-label="Página anterior">
                <ChevronLeft aria-hidden="true" />
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(pagina + 1)} aria-label="Próxima página">
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          </nav>
        </Card>
      )}
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold leading-tight">Notas de Compra</h1>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {dados ? contagemNotas(dados.total) : ' '}
            </p>
          </div>
          {botaoImportar}
        </CardContent>
      </Card>

      <div className="sticky top-0 z-10 flex flex-wrap items-end gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros da lista">
        <div className="flex min-w-[16rem] flex-col gap-1">
          <label htmlFor="filtro-fornecedor" className="text-xs font-medium text-slate-600">
            Fornecedor
          </label>
          <div className="flex items-center gap-1">
            <div className="flex-1">
              <FornecedorCombobox value={filtros.fornecedor} onChange={(fornecedor) => alterarFiltros({ fornecedor })} id="filtro-fornecedor" />
            </div>
            {filtros.fornecedor && (
              <Button type="button" variant="ghost" size="icon" aria-label="Limpar fornecedor" onClick={() => alterarFiltros({ fornecedor: null })}>
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-situacao" className="text-xs font-medium text-slate-600">
            Situação
          </label>
          <CampoSelect
            id="filtro-situacao"
            value={filtros.situacao}
            onValueChange={(valor) => alterarFiltros({ situacao: valor as SituacaoNotaCompra | '' })}
            options={[{ value: '', label: 'Todas as situações' }, ...SITUACOES_NOTA_COMPRA.map((situacao) => ({ value: situacao, label: rotuloSituacaoNota(situacao) }))]}
            className="w-56"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-de" className="text-xs font-medium text-slate-600">
            De
          </label>
          <DatePicker id="filtro-de" value={filtros.de} onValueChange={(value) => alterarFiltros({ de: value })} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-ate" className="text-xs font-medium text-slate-600">
            Até
          </label>
          <DatePicker id="filtro-ate" value={filtros.ate} onValueChange={(value) => alterarFiltros({ ate: value })} />
        </div>

        {comFiltro && (
          <Button type="button" variant="ghost" onClick={limparFiltros}>
            Limpar filtros
          </Button>
        )}
      </div>

      <Tabs
        value={filtros.aba}
        onValueChange={(valor) => {
          setDados(null);
          alterarFiltros({ aba: valor as AbaNotasCompra });
        }}
        className="gap-4"
      >
        <TabsList aria-label="Seções das notas de compra">
          <TabsTrigger value="TODAS" className="min-h-9 px-4">
            Notas
          </TabsTrigger>
          <TabsTrigger value="SEM_PEDIDO" className="min-h-9 px-4">
            {rotuloAbaSemPedido(totalSemPedido)}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="TODAS">{filtros.aba === 'TODAS' ? corpo : null}</TabsContent>
        <TabsContent value="SEM_PEDIDO">{filtros.aba === 'SEM_PEDIDO' ? corpo : null}</TabsContent>
      </Tabs>

      <ImportarNotaDialog aberto={importando} onAbertoChange={setImportando} aoImportar={() => void carregar()} />
    </div>
  );
}

function LinhaNota({ nota, semPedido }: { nota: NotaCompraResumo; semPedido: boolean }) {
  const situacao = apresentacaoSituacaoNota(nota.situacao);
  const Icone = ICONES_SITUACAO[situacao.icone];
  return (
    <tr className="border-t hover:bg-slate-50">
      <td className="px-3 py-2 font-semibold tabular-nums">{nota.serie ? `${nota.numero}-${nota.serie}` : nota.numero}</td>
      <td className="max-w-[20rem] truncate px-3 py-2" title={nota.fornecedorRazaoSocial}>
        {nota.fornecedorRazaoSocial}
      </td>
      <td className="px-3 py-2 tabular-nums">{formatarData(nota.emissao)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(nota.valorTotal)}</td>
      {semPedido ? (
        <>
          <td className="max-w-[24rem] px-3 py-2">
            <span className="line-clamp-2 whitespace-pre-wrap" title={nota.justificativaSemPedido ?? undefined}>
              {nota.justificativaSemPedido ?? '—'}
            </span>
          </td>
          <td className="px-3 py-2">{nota.recebidaPorNome ?? '—'}</td>
        </>
      ) : (
        <>
          <td className="px-3 py-2 tabular-nums">{nota.pedidos.length > 0 ? nota.pedidos.join(', ') : '—'}</td>
          <td className="px-3 py-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
              <Icone className="size-3.5 shrink-0" aria-hidden="true" />
              {situacao.rotulo}
            </span>
          </td>
        </>
      )}
      <td className="px-3 py-2 text-right">
        <Button asChild variant="outline" size="sm">
          <a href={urlConferencia(nota.id)} aria-label={`Abrir nota ${nota.numero}`}>
            Abrir
          </a>
        </Button>
      </td>
    </tr>
  );
}
